/**
 * Mention email payloads — pure builder that turns a comment's parsed
 * @mentions into transactional email payloads for the outbox (Brevo).
 *
 * Pure + honest: it only produces payloads for mentions it can genuinely
 * resolve to a real user email; it never emails the comment's author, and
 * it skips agent personas (`claude:*`) which have no inbox. The caller
 * (`notifyMentions`) supplies the user directory and queues the results —
 * nothing here does I/O or claims anything was sent.
 */

import type { SendEmailPayload } from '../email';

export interface MentionRecipient {
  displayName: string;
  email: string;
}

/**
 * Resolve a roster mention (e.g. "Grant" or "Grant Walker") to a user email
 * by matching the display name, its first token, or the email local-part.
 */
export function resolveMentionEmail(
  name: string,
  users: MentionRecipient[],
): string | null {
  const n = name.trim().toLowerCase();
  if (!n) return null;
  for (const u of users) {
    if (!u.email) continue;
    const dn = (u.displayName || '').toLowerCase();
    const first = dn.split(' ')[0];
    const local = u.email.split('@')[0].toLowerCase();
    if (dn === n || first === n || local === n) return u.email;
  }
  return null;
}

export function mentionEmailPayloads(opts: {
  mentions: string[];
  authorName: string;
  /** Human context, e.g. 'the project "Billing Automation"'. */
  context: string;
  /** Optional deep link included in the body. */
  url?: string;
  users: MentionRecipient[];
}): SendEmailPayload[] {
  const { mentions, authorName, context, url, users } = opts;
  const authorLower = authorName.trim().toLowerCase();
  const seen = new Set<string>();
  const payloads: SendEmailPayload[] = [];

  for (const m of mentions) {
    const ml = m.trim().toLowerCase();
    if (!ml || ml === authorLower) continue; // never email the author
    if (ml.startsWith('claude:')) continue; // agents have no inbox
    const email = resolveMentionEmail(m, users);
    if (!email || seen.has(email.toLowerCase())) continue;
    seen.add(email.toLowerCase());

    const subject = `${authorName} mentioned you in ${context}`;
    const openLine = url ? `\n\nOpen it: ${url}` : '';
    const textContent = `${authorName} mentioned you in ${context}.${openLine}`;
    const htmlContent =
      `<p><strong>${authorName}</strong> mentioned you in ${context}.</p>` +
      (url ? `<p><a href="${url}">Open it</a></p>` : '');
    payloads.push({ to: email, subject, textContent, htmlContent });
  }
  return payloads;
}
